import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet } from "../../api";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Button } from "../../components/ui/button";
import { MASTER_TYPE, MASTER_VISIBILITY, SCHEDULE, TIER, adminPost, useCan } from "./api";
import { Drawer, ListView, inputCls, labelCls } from "./parts";
import { MASTER_COLUMNS, PROVIDER, masterRow } from "./rows";

const BLANK = {
  username: "",
  tier: "STARTER",
  leverage: "",
  name: "",
  cutPercent: "",
  schedule: "WEEKLY",
  minimumEquity: "",
  type: "customer",
  // 030: no preselect, so nobody shares or hides by accident.
  sharesPositions: "",
  // v1.17.0 (031): "off" or "amount", no preselect; `accessFee` is the amount's text.
  feeMode: "",
  accessFee: "",
  // v1.24.0 (039): required; PCX first and preselected.
  provider: "PCX",
  // v1.25.0 (040): optional on the server, 0 (off) by default.
  sponsorPercent: "0",
};

const PROVIDERS = [
  { value: "PCX", label: "PCX" },
  { value: "LP_PRIME", label: "LP Prime" },
];

const FEE_MODES = [
  { value: "", label: "Choose…" },
  { value: "off", label: "Free" },
  { value: "amount", label: "Charge a fee" },
];

const SHARES = [
  { value: "", label: "Choose…" },
  { value: "true", label: "Share" },
  { value: "false", label: "Don't share" },
];

const int = (v, min, max) => /^\d+$/.test(v) && +v >= min && +v <= max;

// The handoff 020 rules, checked before a call that opens a real account.
// The backend checks them again; its message wins when the two disagree.
export function validateMaster(f) {
  const errors = {};
  if (!f.username.trim()) errors.username = "Required";
  // Backend allows 1 to 2000 (v1.37.0); Ginox caps masters at 500 like users.
  if (!int(f.leverage, 1, 500)) errors.leverage = "Whole number 1 to 500";
  const name = f.name.trim();
  if (!/^[A-Za-z0-9._-]{3,30}$/.test(name))
    errors.name = "3 to 30 of A-Z a-z 0-9 . _ - with no spaces";
  else if (/ginox/i.test(name)) errors.name = "Must not contain \"ginox\"";
  if (!int(f.cutPercent, 5, 80)) errors.cutPercent = "Whole number 5 to 80";
  if (!int(f.sponsorPercent, 0, 100)) errors.sponsorPercent = "Whole number 0 to 100";
  if (!/^\d+(\.\d{1,2})?$/.test(f.minimumEquity) || +f.minimumEquity < 100)
    errors.minimumEquity = "At least 100, at most 2 decimals";
  if (f.type !== "customer" && f.type !== "signal") errors.type = "Customer or signal";
  if (f.sharesPositions !== "true" && f.sharesPositions !== "false") errors.sharesPositions = "Choose one";
  if (f.feeMode !== "off" && f.feeMode !== "amount") errors.feeMode = "Choose one";
  else if (f.feeMode === "amount") {
    const fee = f.accessFee.trim();
    if (!/^\d{1,5}(\.\d{1,2})?$/.test(fee) || +fee < 1 || +fee > 10000)
      errors.accessFee = "1 to 10000, at most 2 decimals";
  }
  if (f.provider !== "PCX" && f.provider !== "LP_PRIME") errors.provider = "Choose one";
  // LP Prime has no groups above 1:300; the backend would silently store PCX.
  else if (f.provider === "LP_PRIME" && +f.leverage > 300) errors.provider = "LP Prime only up to 1:300";
  return errors;
}

// The body is strict: exactly these twelve keys, numbers as integers and
// minimumEquity as a decimal string. `type` is required since v1.1.0.
// Shared leverage: `minimumLeverage` is gone and now a 400; `leverage` sets the
// master, its cover and every copier. v1.16.0 (030): `sharesPositions` is a
// required JSON boolean; "true" or 1 is a 400. v1.17.0 (031): `accessFee` is required,
// "off" or the amount as a STRING; "0", null or a number is a 400. v1.24.0 (039):
// `provider` is required, LP_PRIME or PCX in capitals. v1.25.0 (040): `sponsorPercent`
// is a strict integer; "10" is a 400.
const toBody = (f) => ({
  username: f.username.trim(),
  tier: f.tier,
  leverage: +f.leverage,
  name: f.name.trim(),
  cutPercent: +f.cutPercent,
  schedule: f.schedule,
  minimumEquity: f.minimumEquity,
  type: f.type,
  sharesPositions: f.sharesPositions === "true",
  accessFee: f.feeMode === "off" ? "off" : f.accessFee.trim(),
  provider: f.provider,
  sponsorPercent: +f.sponsorPercent,
});

function Field({ label, name, form, setForm, errors, error = errors[name], options, disabled, placeholder }) {
  const onChange = (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {options ? (
        <select value={form[name]} onChange={onChange} className={inputCls}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input value={form[name]} onChange={onChange} disabled={disabled}
          placeholder={placeholder} className={inputCls} />
      )}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

// POST /masters/admin: opens a real account for a user at zero balance and
// makes it a master. Pass `username` to lock the owner (user detail page).
export function CreateMasterDrawer({ open, onClose, username }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...BLANK, username: username ?? "" });
  const [errors, setErrors] = useState({});
  const m = useMutation({
    mutationFn: (body) => adminPost("/masters/admin", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["forex-admin"] }),
  });

  // 020: an unknown username is a 502 only after the call, so confirm it first.
  // The dashboard's withdraw status matches the username exactly: 404 is no such user.
  const typed = form.username.trim();
  const [looked, setLooked] = useState(typed);
  useEffect(() => {
    const t = setTimeout(() => setLooked(typed), 400);
    return () => clearTimeout(t);
  }, [typed]);
  const lookup = useQuery({
    queryKey: ["ginox-user", looked],
    queryFn: () => apiGet("/api/admin/user/get-withdraw-status", { params: { username: looked } }),
    enabled: !username && !!looked,
    retry: false,
  });
  const unknown = !username && lookup.error?.response?.status === 404;
  // Any other failure leaves the button on: the backend refuses an unknown user anyway.
  const checking = !username && !!typed && (typed !== looked || lookup.isFetching);

  const close = () => {
    setForm({ ...BLANK, username: username ?? "" });
    setErrors({});
    m.reset();
    onClose();
  };
  const submit = (e) => {
    e.preventDefault();
    const found = validateMaster(form);
    setErrors(found);
    if (Object.keys(found).length === 0) m.mutate(toBody(form));
  };
  const f = { form, setForm, errors };

  return (
    <Drawer open={open} onClose={close} title="Create master">
      {m.data ? (
        <div className="space-y-4 text-sm">
          <p>
            Master <span className="font-mono">{m.data.name}</span> is open on MT5 login{" "}
            <span className="font-mono">{m.data.login}</span> as a {m.data.type} master on {PROVIDER[m.data.provider] ?? m.data.provider} at{" "}
            <span className="font-mono">1:{m.data.leverage}</span>. Its cover
            opened beside it at zero balance.{" "}
            {m.data.sharesPositions ? "Its copiers see its open positions." : "Its copiers do not see its open positions."}{" "}
            {m.data.accessFee === "off" ? "Free to connect." : `Access fee: ${m.data.accessFee} USD per connect.`}
            {m.data.sponsorPercent > 0 && ` Sponsor share: ${m.data.sponsorPercent}%.`}
          </p>
          <p className="text-amber-400">The user must fund this account before it copies anything.</p>
          <Button size="sm" onClick={close}>Done</Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Opens a real account for the user at zero balance and makes it a master. Nothing leaves
            their wallet.
          </p>
          <Field label="Ginox Username" name="username" disabled={!!username} {...f}
            error={errors.username || (unknown && "No Ginox user with this username")} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Account (tier)" name="tier" options={TIER.slice(1)} {...f} />
            <Field label="Leverage (1:N)" name="leverage" placeholder="100" {...f} />
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            The cover and every copier use this leverage.
          </p>
          <Field label="Master name" name="name" placeholder="AlphaFX" {...f} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type" name="type" options={MASTER_TYPE.slice(1)} {...f} />
            <Field label="Provider" name="provider" options={PROVIDERS} {...f} />
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            Type is fixed once made. Signal is Ginox&apos;s own master: its own trades stay on the B-book.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Profit share %" name="cutPercent" placeholder="20" {...f} />
            <Field label="Schedule" name="schedule" options={SCHEDULE} {...f} />
            <Field label="Minimum deposit" name="minimumEquity" placeholder="100" {...f} />
            <Field label="Sponsor share %" name="sponsorPercent" placeholder="0" {...f} />
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">
            Sponsor share comes out of the master&apos;s share, for copiers its BD referred. 0 is off.
          </p>
          <div>
            <Field label="Copiers see its open positions" name="sharesPositions" options={SHARES} {...f} />
            <p className="mt-1 text-xs text-muted-foreground">Only the owner can change this later.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Access fee per connect" name="feeMode" options={FEE_MODES} {...f} />
            {form.feeMode === "amount" && <Field label="Fee (USD)" name="accessFee" placeholder="50" {...f} />}
          </div>
          {m.error && (
            <p className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {m.error.message}
              {/* 020: 502 is an unknown username, or the trading server gave no login or time. */}
              {m.error.status === 502 && " The username may not exist, or the trading server did not open the account."}
            </p>
          )}
          <Button type="submit" size="sm" disabled={m.isPending || checking || unknown}>
            {m.isPending ? "Creating…" : "Create master"}
          </Button>
        </form>
      )}
    </Drawer>
  );
}

export default function Masters() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const can = useCan();
  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Copy trading"
      accent="masters"
      description="Every master with its owner, type, cover, terms, copiers and what it has earned. Click the copier count for its connections. Hidden masters are off the users' master list but keep trading and copying."
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      {can("masters.open") && <Button size="sm" onClick={() => setCreating(true)}>+ Create master</Button>}
      <ListView
        path="/masters/admin"
        empty={{ name: "", username: "", login: "", tier: "", type: "", hidden: "", sort: "created_at", order: "desc" }}
        fields={[
          { name: "name", label: "Name contains" },
          { name: "username", label: "Owner username" },
          { name: "login", label: "Master login" },
          { name: "tier", label: "Tier", options: TIER },
          { name: "type", label: "Type", options: MASTER_TYPE },
          { name: "hidden", label: "Visibility", options: MASTER_VISIBILITY },
          { name: "sort", label: "Sort", options: [
            { value: "created_at", label: "Newest" }, { value: "copiers", label: "Copiers" }, { value: "earned", label: "Earned" },
          ] },
          { name: "order", label: "Order", options: [
            { value: "desc", label: "Descending" }, { value: "asc", label: "Ascending" },
          ] },
        ]}
        columns={MASTER_COLUMNS}
        row={masterRow}
        label="masters"
      />
      <CreateMasterDrawer open={creating} onClose={() => setCreating(false)} />
    </QueueLayout>
  );
}
